// Where an AI agent's sign-in lands (/connect?request=...): the person sees
// which agent is asking, what it will be able to do, and approves or cancels.
// Approving sends the browser back to the agent with a one-time code.
// If they weren't signed in, AuthProvider remembered this page and sign-in
// brings them back here (lib/returnTo).
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { AGENT_CANT, agentCan, agentsApi } from '../lib/agents';
import { clearReturnTo } from '../lib/returnTo';

export default function ConnectAgentPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // 'approve' | 'deny' while sending

  useEffect(() => {
    // Inside another site's frame, someone could trick a click on Approve
    // (clickjacking). nginx also refuses to be framed here; this covers local dev.
    if (window.top !== window.self) {
      setError('For your safety, open this page in its own tab.');
      return;
    }
    if (!requestId) {
      setError('This link is missing its sign-in. Start again from your AI agent.');
      return;
    }
    // Clear the return-after-sign-in only once signed in and loaded: this page
    // can open for a moment before sign-in redirects, and clearing then would
    // send the person to the dashboard afterwards.
    agentsApi.request(requestId)
      .then((r) => { clearReturnTo(); setRequest(r); })
      .catch((e) => {
        if (e.response?.status === 401) return; // signing in first; AuthProvider takes them to /login
        clearReturnTo();
        setError(e.response?.data?.detail || 'This sign-in has expired. Start again from your AI agent.');
      });
  }, [requestId]);

  const answer = async (choice) => {
    setBusy(choice);
    try {
      const redirect = choice === 'approve' ? await agentsApi.approve(requestId) : await agentsApi.deny(requestId);
      window.location.assign(redirect);
    } catch (e) {
      setBusy(null);
      setError(e.response?.data?.detail || 'That didn’t work. Start again from your AI agent.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="connect-agent">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4 overflow-hidden">
            <Logo className="w-16 h-16" />
          </div>
          <CardTitle className="text-2xl">
            {request ? `Connect ${request.agent} to No Half Days` : 'Connect an AI agent'}
          </CardTitle>
          {request && (
            <CardDescription>
              {request.agent} will work in your account, {request.email}.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive" data-testid="connect-error">
              {error}
            </div>
          )}

          {!request && !error && (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          )}

          {request && (
            <>
              <div className="space-y-2">
                <p className="eyebrow">it can</p>
                <ul className="space-y-1.5 text-sm">
                  {agentCan(request.on_this_plan, request.can_create).map((line) => (
                    <li key={line} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-moss" />{line}</li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <p className="eyebrow">it can’t</p>
                <ul className="space-y-1.5 text-sm">
                  {AGENT_CANT.map((line) => (
                    <li key={line} className="flex gap-2"><X className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />{line}</li>
                  ))}
                </ul>
              </div>
              {!request.can_create && (
                <p className="text-sm">
                  Your trial has ended, so for now it can only look things up. Subscribe in{' '}
                  <Link to="/settings?tab=billing" className="text-clay hover:underline">Settings → Billing</Link>{' '}
                  and it can draft and change things too.
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                You’ll go back to {request.returns_to}. You can disconnect it any time in{' '}
                <Link to="/settings?tab=mcp" className="text-clay hover:underline">Settings → MCP</Link>.
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <Button variant="outline" className="flex-1" disabled={!!busy} onClick={() => answer('deny')} data-testid="connect-deny">
                  {busy === 'deny' && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Cancel
                </Button>
                <Button className="flex-1" disabled={!!busy} onClick={() => answer('approve')} data-testid="connect-approve">
                  {busy === 'approve' && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Approve
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
